import { coffreFileExtension, coffreFileIconKind } from "./coffreFileIcons.jsx";
import { COFFRE_PREVIEW_EML_MAX_BYTES } from "./coffreEml.js";
import { coffreIsMarkdownFilename } from "./coffreMarkdown.js";
import { coffreCanOpenInOffice } from "./coffreOffice.js";

/** Taille max chargée en aperçu (images via blob). */
export const COFFRE_PREVIEW_MAX_BYTES = 50 * 1024 * 1024;

/** Vidéos : streaming serveur (Range HTTP), plafond élevé. */
export const COFFRE_PREVIEW_VIDEO_MAX_BYTES = 2 * 1024 * 1024 * 1024;

/** Taille max aperçu texte (txt, md, log…) — chargé en mémoire côté client. */
export const COFFRE_PREVIEW_TEXT_MAX_BYTES = 5 * 1024 * 1024;

const PREVIEW_EXT_BLOCK = new Set(["svg", "heic", "heif"]);

/** .txt reste l’aperçu texte natif. */
const OFFICE_PANE_SKIP_EXT = new Set(["txt"]);

/**
 * @param {{ kind?: string, name?: string, size?: number | null }} entry
 */
export function coffreCanPreviewFile(entry) {
  if (!entry || entry.kind !== "file") return false;
  const ext = coffreFileExtension(entry.name);
  if (ext === "eml") {
    if (entry.size != null && entry.size > COFFRE_PREVIEW_EML_MAX_BYTES) return false;
    return true;
  }
  if (ext && PREVIEW_EXT_BLOCK.has(ext)) return false;
  const kind = coffreFileIconKind(entry.name);
  if (kind === "text") {
    if (entry.size != null && entry.size > COFFRE_PREVIEW_TEXT_MAX_BYTES) return false;
    return true;
  }
  if (kind !== "image" && kind !== "pdf" && kind !== "video" && kind !== "audio") return false;
  if (entry.size != null && kind !== "video" && kind !== "audio") {
    if (entry.size > COFFRE_PREVIEW_MAX_BYTES) return false;
  }
  if (entry.size != null && (kind === "video" || kind === "audio") && entry.size > COFFRE_PREVIEW_VIDEO_MAX_BYTES) {
    return false;
  }
  return true;
}

/** @param {string} filename */
export function coffrePreviewMediaKind(filename) {
  if (coffreFileExtension(filename) === "eml") return "eml";
  if (coffreIsMarkdownFilename(filename)) return "markdown";
  const k = coffreFileIconKind(filename);
  if (k === "text") return "text";
  if (k === "image") return "image";
  if (k === "pdf") return "pdf";
  if (k === "video") return "video";
  if (k === "audio") return "audio";
  if (coffreIsOfficePreviewFilename(filename)) return "office";
  return null;
}

export function coffreIsOfficePreviewFilename(filename) {
  const ext = coffreFileExtension(filename);
  if (!ext || OFFICE_PANE_SKIP_EXT.has(ext)) return false;
  return coffreCanOpenInOffice({ kind: "file", name: filename, size: null });
}

export function coffreCanPreviewOfficeDoc(entry, onlyofficeConfigured) {
  if (!onlyofficeConfigured) return false;
  if (!coffreCanOpenInOffice(entry)) return false;
  const ext = coffreFileExtension(entry.name);
  if (OFFICE_PANE_SKIP_EXT.has(ext)) return false;
  return true;
}

export function coffreCanPreviewInPane(entry, onlyofficeConfigured) {
  return coffreCanPreviewFile(entry) || coffreCanPreviewOfficeDoc(entry, onlyofficeConfigured);
}

/** Vidéo ou audio : streaming + lecteur intégré. */
export function coffrePreviewIsPlayableMedia(filename) {
  const k = coffrePreviewMediaKind(filename);
  return k === "video" || k === "audio";
}

/** Fichiers .txt modifiables dans l’aperçu Coffre. */
export function coffreIsEditableTextFile(name) {
  return coffreFileExtension(name) === "txt";
}

/** Markdown éditable (BlockNote) — hors lecture seule partage. */
export function coffreIsEditableMarkdownFile(name) {
  return coffreIsMarkdownFilename(name);
}
